import { ExternalLink, FileText } from "lucide-react";
import type { Product, ProductOffer, ProductVariant } from "@sakura-tech-lab/dashboard-api";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { StructuredDescription } from "./StructuredDescription";
import {
  formatDataValue,
  formatDateTime,
  getVariantOffers,
  humanizeKey,
  pickLocalized,
} from "./product-detail-utils";

interface ProductSourceSheetProps {
  readonly product: Product;
  readonly selectedVariant: ProductVariant | null;
  readonly language: string;
}

interface SourceField {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly href?: string;
}

interface SourceRecord {
  readonly label: string;
  readonly fields: readonly SourceField[];
}

/**
 * "Source information" trigger button + right-side slide-over panel showing the
 * original supplier content (raw name/description/attributes) and record
 * metadata. Kept out of the main page flow since it is reference material.
 */
export function ProductSourceSheet({
  product,
  selectedVariant,
  language,
}: ProductSourceSheetProps) {
  const originalName = pickLocalized(selectedVariant?.originalName, language);
  const originalDescription = pickLocalized(selectedVariant?.originalDescription, language);
  const supplierAttributes = {
    ...(product.customProperties ?? {}),
    ...(selectedVariant?.originalAttributes ?? {}),
  };
  const attributeEntries = Object.entries(supplierAttributes).flatMap(([key, value]) => {
    const formatted = formatDataValue(value);
    return formatted ? [[key, formatted] as const] : [];
  });
  const sourceRecords = buildSourceRecords(product, selectedVariant);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <FileText aria-hidden="true" className="h-4 w-4" />
          Source information
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Source information</SheetTitle>
          <SheetDescription>Original supplier content and record metadata.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {originalName ? (
            <section className="space-y-2" aria-labelledby="original-name-heading">
              <h3 id="original-name-heading" className="text-sm font-semibold text-foreground">
                Original name
              </h3>
              <p className="break-words text-sm leading-relaxed text-foreground">{originalName}</p>
            </section>
          ) : null}

          {originalDescription ? (
            <section className="space-y-2" aria-labelledby="original-description-heading">
              <h3 id="original-description-heading" className="text-sm font-semibold text-foreground">
                Original description
              </h3>
              <StructuredDescription text={originalDescription} />
            </section>
          ) : null}

          {attributeEntries.length > 0 ? (
            <section className="space-y-3" aria-labelledby="supplier-attributes-heading">
              <div className="flex items-center justify-between gap-2">
                <h3 id="supplier-attributes-heading" className="text-sm font-semibold text-foreground">
                  Supplier attributes
                </h3>
                <Badge variant="outline">{attributeEntries.length}</Badge>
              </div>
              <dl className="grid gap-3 sm:grid-cols-2">
                {attributeEntries.map(([key, value]) => (
                  <SourceValue key={key} label={humanizeKey(key)} value={value} />
                ))}
              </dl>
            </section>
          ) : null}

          <section className="space-y-3" aria-labelledby="source-records-heading">
            <h3 id="source-records-heading" className="text-sm font-semibold text-foreground">
              Source records
            </h3>
            <div className="grid gap-4">
              {sourceRecords.map((record, index) => (
                <article key={`${record.label}-${index}`} className="min-w-0 rounded-lg border bg-muted/20 p-4">
                  <h4 className="text-sm font-semibold text-foreground">{record.label}</h4>
                  <dl className="mt-3 space-y-2.5">
                    {record.fields.map((field) => (
                      <div key={`${field.label}-${field.value}`} className="grid min-w-0 gap-1 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3">
                        <dt className="text-xs text-muted-foreground">{field.label}</dt>
                        <dd
                          className={cn(
                            "min-w-0 break-words text-sm text-foreground",
                            field.mono && "break-all font-mono text-xs",
                          )}
                        >
                          {field.href ? (
                            <a
                              href={field.href}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex max-w-full items-start gap-1 text-primary underline-offset-4 hover:underline"
                            >
                              <span className="break-all">{field.value}</span>
                              <ExternalLink aria-hidden="true" className="mt-0.5 h-3 w-3 shrink-0" />
                            </a>
                          ) : (
                            field.value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </article>
              ))}
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SourceValue({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="min-w-0 rounded-md border bg-muted/20 p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

function buildSourceRecords(
  product: Product,
  selectedVariant: ProductVariant | null,
): SourceRecord[] {
  const records: SourceRecord[] = [
    {
      label: "Product",
      fields: compactFields([
        { label: "Product ID", value: product.id, mono: true },
        { label: "External reference", value: product.externalRef, mono: true, href: product.url },
        { label: "SKU", value: product.sku, mono: true },
        { label: "Version hash", value: product.versionHash, mono: true },
        dateField("Created", product.createdAt),
        dateField("Updated", product.updatedAt),
        dateField("Deleted", product.deletedAt),
      ]),
    },
  ];

  if (selectedVariant) {
    records.push({
      label: "Selected variant",
      fields: compactFields([
        { label: "Variant ID", value: selectedVariant.id, mono: true },
        dateField("Created", selectedVariant.createdAt),
        dateField("Updated", selectedVariant.updatedAt),
        dateField("Deleted", selectedVariant.deletedAt),
      ]),
    });
  }

  for (const offer of getVariantOffers(product, selectedVariant)) {
    records.push(offerSourceRecord(offer));
  }

  return records;
}

function offerSourceRecord(offer: ProductOffer): SourceRecord {
  return {
    label: offer.vendor ? `Offer · ${offer.vendor}` : "Offer",
    fields: compactFields([
      { label: "Offer ID", value: offer.id, mono: true },
      { label: "Vendor ID", value: offer.vendorId ?? "", mono: true },
      { label: "External reference", value: offer.externalRef ?? "", mono: true, href: offer.url },
      { label: "SKU", value: offer.sku, mono: true },
      { label: "Version hash", value: offer.versionHash, mono: true },
      dateField("Created", offer.createdAt),
      dateField("Updated", offer.updatedAt),
      dateField("Deleted", offer.deletedAt),
    ]),
  };
}

function dateField(label: string, value: Date | null | undefined): SourceField {
  return { label, value: formatDateTime(value) ?? "" };
}

function compactFields(fields: readonly SourceField[]): SourceField[] {
  return fields.filter((field) => field.value.trim());
}
